import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://edassist.co.in';

export const JsonLd = ({ data }) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

export const OrganizationSchema = () => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EdAssist",
    "url": SITE_URL,
    "logo": `${SITE_URL}/edassist-icon-square.png`,
    "description": "India's premium education job portal connecting schools and institutions with qualified educators.",
    "foundingDate": "2025",
    "industry": "Education / Recruitment",
    "areaServed": { "@type": "Country", "name": "India" },
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "contact@edassist.co.in",
      "availableLanguage": ["English", "Hindi"],
    },
  }} />
);

export const WebSiteSchema = () => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "EdAssist",
    "url": SITE_URL,
    "description": "India's premium education job portal. Find teaching jobs, build your resume, and connect with top schools.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": { "@type": "EntryPoint", "urlTemplate": `${SITE_URL}/jobs?search={search_term_string}` },
      "query-input": "required name=search_term_string",
    },
  }} />
);

export const JobPostingSchema = ({ job }) => {
  if (!job || job.status !== 'published') return null;
  if (job.deadline && new Date(job.deadline) < new Date()) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description || job.title,
    "datePosted": job.created_at?.slice(0, 10),
    "employmentType": mapEmploymentType(job.job_type),
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.company_name || job.institution_name || "EdAssist Partner",
      "sameAs": SITE_URL,
      "logo": job.company_logo || `${SITE_URL}/edassist-icon-square.png`,
    },
    "applicantLocationRequirements": { "@type": "Country", "name": "India" },
  };
  if (job.location) {
    const parts = job.location.split(',').map(s => s.trim()).filter(Boolean);
    const hasCountry = parts.length && /india/i.test(parts[parts.length - 1]);
    const geoParts = hasCountry ? parts.slice(0, -1) : parts;
    const address = { "@type": "PostalAddress", "addressCountry": "IN" };
    if (geoParts.length >= 2) {
      address.addressRegion = geoParts[geoParts.length - 1];
      address.addressLocality = geoParts[geoParts.length - 2];
      if (geoParts.length > 2) address.streetAddress = geoParts.slice(0, -2).join(', ');
    } else {
      address.addressLocality = job.location;
    }
    data.jobLocation = { "@type": "Place", "address": address };
  }
  if (job.salary_min || job.salary_max) {
    data.baseSalary = {
      "@type": "MonetaryAmount", "currency": "INR",
      "value": { "@type": "QuantitativeValue", "minValue": job.salary_min || 0, "maxValue": job.salary_max || 0, "unitText": "YEAR" },
    };
  }
  if (job.deadline) {
    data.validThrough = job.deadline;
  } else if (job.created_at) {
    const fallback = new Date(job.created_at);
    fallback.setDate(fallback.getDate() + 90);
    data.validThrough = fallback.toISOString();
  }
  if (job.experience_level) data.experienceRequirements = job.experience_level;
  if (job.subject) data.skills = job.subject;
  return <JsonLd data={data} />;
};

export const BlogPostingSchema = ({ blog }) => {
  if (!blog) return null;
  return (
    <JsonLd data={{
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": blog.excerpt || blog.title,
      "image": blog.cover_image || undefined,
      "datePublished": blog.created_at?.slice(0, 10),
      "dateModified": (blog.updated_at || blog.created_at)?.slice(0, 10),
      "author": { "@type": "Person", "name": blog.author_name || "EdAssist" },
      "publisher": {
        "@type": "Organization", "name": "EdAssist",
        "logo": { "@type": "ImageObject", "url": `${SITE_URL}/edassist-icon-square.png` },
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE_URL}/blogs/${blog.blog_id}` },
    }} />
  );
};

export const BreadcrumbSchema = ({ items }) => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": item.url ? `${SITE_URL}${item.url}` : undefined,
    })),
  }} />
);

function mapEmploymentType(type) {
  const map = { 'full-time': 'FULL_TIME', 'part-time': 'PART_TIME', 'contract': 'CONTRACTOR', 'temporary': 'TEMPORARY', 'internship': 'INTERN', 'freelance': 'OTHER' };
  return map[(type || '').toLowerCase()] || 'FULL_TIME';
}
